"use client";

import { useUser } from "@repo/auth/client";
import { useEffect } from "react";
import { analytics } from "./client";

/**
 * Runtime subset of the posthog-js conversations (support widget) API, typed
 * locally so visibility control doesn't depend on the exact installed SDK
 * version exposing these methods.
 */
type ConversationsApi = {
	show?: () => void;
	hide?: () => void;
	isVisible?: () => boolean;
};

// The conversations extension loads asynchronously via remote config, so we
// re-apply the desired visibility on a short bounded interval until the widget
// has loaded and settled into the right state.
const RETRY_INTERVAL_MS = 200;
const MAX_RETRIES = 60;

/**
 * Shows the PostHog support widget only to authenticated users.
 *
 * posthog-js renders the widget globally once it's enabled in the dashboard, so
 * without this it would also appear on signed-out pages (sign-in / sign-up /
 * marketing). Mounted once, app-wide, inside the analytics provider.
 */
export const ConversationsVisibility = () => {
	const { isLoaded, isSignedIn } = useUser();

	useEffect(() => {
		if (!isLoaded) {
			return;
		}

		const desired = Boolean(isSignedIn);

		const sync = () => {
			const conversations = (
				analytics as unknown as { conversations?: ConversationsApi }
			).conversations;

			// Extension not loaded yet — a later retry will pick it up.
			if (!conversations?.show || !conversations.hide) {
				return;
			}

			const visible = conversations.isVisible?.() ?? false;

			if (desired && !visible) {
				conversations.show();
			} else if (!desired && visible) {
				conversations.hide();
			}
		};

		sync();

		let attempts = 0;
		const interval = setInterval(() => {
			attempts += 1;
			sync();
			if (attempts >= MAX_RETRIES) {
				clearInterval(interval);
			}
		}, RETRY_INTERVAL_MS);

		return () => clearInterval(interval);
	}, [isLoaded, isSignedIn]);

	return null;
};
